import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  VIBE_TRIGGER_EVENT,
  VIBE_DISMISS_EVENT,
  VIBE_VISIBILITY_EVENT,
  VibeTriggerEvent,
  vibeService,
} from '../../services/VibeService';
import './VibeDrawer.css';

type Edge = 'left' | 'right' | 'top' | 'bottom';

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY_EDGE = 'vibe-drawer-edge';
const STORAGE_KEY_QUIT = 'vibe-drawer-quit';
const STORAGE_KEY_POSITION = 'vibe-drawer-position';

const VibeDrawer: React.FC = () => {
  const [isQuit, setIsQuit] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_QUIT);
    return stored === 'true';
  });
  
  const [currentEdge, setCurrentEdge] = useState<Edge>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_EDGE) as Edge;
    return stored || 'left';
  });
  
  const [edgePosition, setEdgePosition] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_POSITION);
    return stored ? parseFloat(stored) : 50;
  });
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentGif, setCurrentGif] = useState<string | null>(null);
  const [isGiphy, setIsGiphy] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<Position | null>(null);
  
  const drawerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<Position | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragThresholdRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_EDGE, currentEdge);
  }, [currentEdge]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_QUIT, String(isQuit));
  }, [isQuit]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_POSITION, String(edgePosition));
  }, [edgePosition]);

  useEffect(() => {
    const handleVibeTrigger = (event: CustomEvent<VibeTriggerEvent>) => {
      if (isQuit) return;
      
      const { gif, duration } = event.detail;
      setCurrentGif(gif);
      setIsGiphy(gif.includes('giphy.com'));
      setIsExpanded(true);

      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }

      autoCloseTimerRef.current = setTimeout(() => {
        setIsExpanded(false);
        setCurrentGif(null);
      }, duration);
    };

    window.addEventListener(VIBE_TRIGGER_EVENT, handleVibeTrigger as EventListener);
    return () => {
      window.removeEventListener(VIBE_TRIGGER_EVENT, handleVibeTrigger as EventListener);
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, [isQuit]);

  useEffect(() => {
    const handleVisibilityChange = (event: CustomEvent<{ visible: boolean }>) => {
      setIsQuit(!event.detail.visible);
    };

    window.addEventListener(VIBE_VISIBILITY_EVENT, handleVisibilityChange as EventListener);
    return () => {
      window.removeEventListener(VIBE_VISIBILITY_EVENT, handleVisibilityChange as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleDismiss = () => {
      setIsExpanded(false);
      setCurrentGif(null);
      setIsGiphy(false);
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };

    window.addEventListener(VIBE_DISMISS_EVENT, handleDismiss);
    return () => {
      window.removeEventListener(VIBE_DISMISS_EVENT, handleDismiss);
    };
  }, []);

  const calculateNearestEdge = useCallback((x: number, y: number): { edge: Edge; position: number } => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    const distanceToLeft = x;
    const distanceToRight = windowWidth - x;
    const distanceToTop = y;
    const distanceToBottom = windowHeight - y;
    
    const minDistance = Math.min(distanceToLeft, distanceToRight, distanceToTop, distanceToBottom);
    
    if (minDistance === distanceToLeft) {
      return { edge: 'left', position: (y / windowHeight) * 100 };
    } else if (minDistance === distanceToRight) {
      return { edge: 'right', position: (y / windowHeight) * 100 };
    } else if (minDistance === distanceToTop) {
      return { edge: 'top', position: (x / windowWidth) * 100 };
    } else {
      return { edge: 'bottom', position: (x / windowWidth) * 100 };
    }
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    dragStartRef.current = { x: clientX, y: clientY };
    dragThresholdRef.current = false;
    setDragPosition({ x: clientX, y: clientY });
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current) return;
      
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      
      const dx = clientX - dragStartRef.current.x;
      const dy = clientY - dragStartRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 5) {
        dragThresholdRef.current = true;
        setIsDragging(true);
      }
      
      if (dragThresholdRef.current) {
        setDragPosition({ x: clientX, y: clientY });
      }
    };

    const handleMouseUp = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current) return;
      
      const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
      const clientY = 'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY;
      
      if (dragThresholdRef.current) {
        const { edge, position } = calculateNearestEdge(clientX, clientY);
        setCurrentEdge(edge);
        setEdgePosition(Math.max(10, Math.min(90, position)));
      }
      
      setIsDragging(false);
      setDragPosition(null);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [calculateNearestEdge]);

  const handleQuit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsQuit(true);
    setIsExpanded(false);
    setCurrentGif(null);
    setIsGiphy(false);
    vibeService.hide();
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(false);
    setCurrentGif(null);
    setIsGiphy(false);
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
  }, []);

  const handleHandleClick = useCallback(async () => {
    if (dragThresholdRef.current) return;
    
    if (!isExpanded) {
      setIsExpanded(true);
      setCurrentGif('/assets/gif_bored.gif');
      setIsGiphy(false);

      const gif = await vibeService.fetchGif('bored');
      setCurrentGif(gif);
      setIsGiphy(gif.includes('giphy.com'));
      
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
      
      autoCloseTimerRef.current = setTimeout(() => {
        setIsExpanded(false);
        setCurrentGif(null);
      }, 6000);
    }
  }, [isExpanded]);

  const getPositionStyles = (): React.CSSProperties => {
    if (isDragging && dragPosition) {
      return {
        position: 'fixed',
        left: dragPosition.x - 40,
        top: dragPosition.y - 40,
        transform: 'none',
        transition: 'none',
      };
    }

    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    switch (currentEdge) {
      case 'left':
        return {
          ...baseStyle,
          left: 0,
          top: `${edgePosition}%`,
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          ...baseStyle,
          right: 0,
          top: `${edgePosition}%`,
          transform: 'translateY(-50%)',
        };
      case 'top':
        return {
          ...baseStyle,
          top: 0,
          left: `${edgePosition}%`,
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          ...baseStyle,
          bottom: 0,
          left: `${edgePosition}%`,
          transform: 'translateX(-50%)',
        };
      default:
        return baseStyle;
    }
  };

  if (isQuit) {
    return null;
  }

  const drawerContent = (
    <div
      ref={drawerRef}
      className={`vibe-drawer vibe-drawer--${currentEdge} ${isExpanded ? 'vibe-drawer--expanded' : ''} ${isDragging ? 'vibe-drawer--dragging' : ''}`}
      style={getPositionStyles()}
    >
      {isDragging ? (
        <div className="vibe-drawer__drag-box">
          <span>the vibe</span>
        </div>
      ) : isExpanded ? (
        <div className="vibe-drawer__expanded-container">
          <div className="vibe-drawer__gif-wrapper" onClick={handleClose} style={{ cursor: 'pointer' }}>
            {currentGif && (
              <img
                src={currentGif}
                alt="Vibe"
                className="vibe-drawer__gif"
                onError={(e) => {
                  console.warn('[VibeDrawer] GIF failed to load, using fallback');
                  e.currentTarget.src = '/assets/gif_bored.gif';
                  setIsGiphy(false);
                }}
              />
            )}
            {isGiphy && <span className="vibe-drawer__giphy-label">GIPHY</span>}
            <span className="vibe-drawer__label">vibe</span>
          </div>
          <button
            className="vibe-drawer__close"
            onClick={handleClose}
            title="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button
            className="vibe-drawer__quit"
            onClick={handleQuit}
            title="Quit Vibe drawer"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <div
          className="vibe-drawer__handle"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={handleHandleClick}
        />
      )}
    </div>
  );

  return ReactDOM.createPortal(drawerContent, document.body);
};

export default VibeDrawer;
